// src/components/game/GameScreen.js - Güncellenmiş
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { db } from '../firebase';
import { doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { useBotLogic } from '../hooks/useBotLogic';
import { GridCell } from './GridCell';
import { PlayerList } from '../components/game/PlayerList';
import { GameControls } from '../components/game/GameControls';
import { GameLog } from '../components/game/GameLog'; // 🔥 YENİ BİLEŞEN EKLENDİ
import { SoundControl } from '../components/SoundControl';
import { WinnerModal } from '../modals/WinnerModal';
import { EliminationModal } from '../modals/EliminationModal';
import { EndTurnModal } from '../modals/EndTurnModal';
import { SelfEliminationModal } from '../modals/SelfEliminationModal';
import { InlinePredictionCard, InlineDiceResultCard } from './InlinePredictionResult';
import { useToast, ToastContainer } from './ToastNotification';
import { useGameLogic } from '../hooks/useGameLogic';
import {
    GRID_W, GRID_H, MAX_ATTACKS_PER_TURN, WINNING_PERCENTAGE, INITIAL_CELLS, MAX_TURNS,
    POWER_CONSTANTS,
} from '../constants/gameConstants';
import { SetupInfoPanel } from '../components/SetupInfoPanel';
import { BattleAnimation, useBattleAnimations } from '../components/BattleAnimation';
import { DynamicMapOverlay } from '../components/DynamicMapOverlay';

const GameScreen = ({ roomCode, currentUser, onGameEnd, isMuted, toggleMute, playSound }) => {
    // ... (Tüm State, Ref, Hook ve Fonksiyon tanımlamaları aynı kalacak)
    // ... (Kodun büyük kısmı değişmediği için buraya tekrar kopyalamıyorum, sadece render kısmını değiştiriyoruz)
    
    // NOT: Yukarıdaki kodların tamamı aynen kalmalı, sadece return kısmındaki JSX yapısı değişecek.

    // ... (State'ler...)
    const [gameState, setGameState] = useState(null);
    const [players, setPlayers] = useState([]);
    const [myPlayerIndex, setMyPlayerIndex] = useState(-1);
    const [selectedCell, setSelectedCell] = useState(null);
    const [selectedSetupCells, setSelectedSetupCells] = useState([]);
    const [showDiceResult, setShowDiceResult] = useState(false);
    const [diceResult, setDiceResult] = useState({
        attacker: '?',
        defender: '?',
        attackerPrediction: '?',
        defenderPrediction: '?',
        attackerScore: 0,
        defenderScore: 0,
        success: false
    });
    const [battleResult, setBattleResult] = useState('');
    const [showWinner, setShowWinner] = useState(false);
    const [winner, setWinner] = useState(null);
    const [selectableCells, setSelectableCells] = useState([]);
    const [isAwaitingPrediction, setIsAwaitingPrediction] = useState(false);
    const [attackTarget, setAttackTarget] = useState(null);
    const [recentBattles, setRecentBattles] = useState([]);
    const [showRules, setShowRules] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [playerStats, setPlayerStats] = useState({});
    const [criticalHit, setCriticalHit] = useState(false);
    const [showEndTurnConfirmation, setShowEndTurnConfirmation] = useState(false);
    const [eliminatedPlayer, setEliminatedPlayer] = useState(null);
    const [showSelfElimination, setShowSelfElimination] = useState(false);

    // ✅ Toast hook'unu ekledik
    const { toasts, showToast, removeToast } = useToast();
    // #endregion

    // #region Ref ve Hook Tanımlamaları
    const { currentBattle, addBattle, completeBattle, hasPendingBattles } = useBattleAnimations();
    const gameListenerRef = useRef(null);
    const botTimeoutRef = useRef(null);
    const prevGameStateRef = useRef(null);
    const prevPlayersRef = useRef(null);
    const rulesPanelRef = useRef(null);
    const gameMusicRef = useRef(null);
    const MAX_LOG_ENTRIES = 10;
    const { leaveGame } = useGameLogic(roomCode, currentUser);

    const mapBackgroundStyle = useMemo(() => ({
        backgroundImage: `url(${window.location.origin}/images/basemap.webp)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundColor: '#1e293bff',
        position: 'relative',
    }), []);

    // #region Yardımcı Fonksiyonlar (Memoize Edilmiş)
    const getIndex = useCallback((x, y) => y * GRID_W + x, []);
    const rollDice = useCallback(() => Math.floor(Math.random() * 6) + 1, []);

    const {
        executeBotSetup,
        executeBotAction
    } = useBotLogic(roomCode, gameState, players, myPlayerIndex, addBattle);

    const updatePlayerStatsAfterBattle = useCallback((attackerIndex, defenderIndex, attackScore, defenseScore, currentStats) => {
        const newStats = { ...currentStats };

        if (!newStats[attackerIndex]) {
            newStats[attackerIndex] = { attackPower: 0, defensePower: 0, totalScore: 0 };
        }
        if (!newStats[defenderIndex]) {
            newStats[defenderIndex] = { attackPower: 0, defensePower: 0, totalScore: 0 };
        }

        newStats[attackerIndex].totalScore += attackScore;
        newStats[defenderIndex].totalScore += defenseScore;

        const atkScore = newStats[attackerIndex].totalScore;
        const defScore = newStats[defenderIndex].totalScore;

        const MAX_ATTACK_POWER = POWER_CONSTANTS.MAX_ATTACK_POWER;
        const MAX_DEFENSE_POWER = POWER_CONSTANTS.MAX_DEFENSE_POWER;

        newStats[attackerIndex].attackPower = Math.min(
            MAX_ATTACK_POWER,
            Math.floor(Math.log2(atkScore + 1) / 1.5)
        );

        newStats[defenderIndex].defensePower = Math.min(
            MAX_DEFENSE_POWER,
            Math.floor(Math.log2(defScore + 1) / 1.8)
        );

        return newStats;
    }, []);

    const getMaxAttacksForCurrentTurn = useCallback(() => {
        return gameState?.turn === 1 ? 2 : MAX_ATTACKS_PER_TURN;
    }, [gameState?.turn]);

    const canPerformAttack = useCallback(() => {
        if (!gameState) return false;
        const currentAttackCount = gameState.attackCount || 0;
        return currentAttackCount < getMaxAttacksForCurrentTurn();
    }, [gameState, getMaxAttacksForCurrentTurn]);

    const checkWinningConditions = useCallback((currentGameState) => {
        if (!currentGameState || !players.length) return null;

        const totalCells = GRID_W * GRID_H;
        const territories = players.map((_, i) =>
            currentGameState.grid.filter(c => c === i).length
        );
        const activePlayers = territories.filter(count => count > 0).length;

        if (activePlayers === 1 && currentGameState.turn > 0) {
            const winnerIndex = territories.findIndex(count => count > 0);
            if (winnerIndex !== -1) {
                return winnerIndex;
            }
        }

        const maxTerritory = Math.max(...territories);
        const winnerIndex = territories.indexOf(maxTerritory);

        if (maxTerritory >= totalCells * WINNING_PERCENTAGE) {
            return winnerIndex;
        }

        if (currentGameState.turn >= MAX_TURNS) {
            return winnerIndex;
        }

        return null;
    }, [players]);

    const toggleFullscreen = useCallback(() => {
        if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen().catch(err => {
                console.warn('Tam ekran hatası:', err);
                showToast('Tam ekran modu açılamadı: ' + err.message, 'error', 3000);
            });
            setIsFullscreen(true);
        } else {
            if (document.exitFullscreen) {
                document.exitFullscreen();
                setIsFullscreen(false);
            }
        }
    }, [showToast]);

    const findNextActivePlayer = useCallback((currentIndex, grid, playerCount) => {
        let nextIndex = (currentIndex + 1) % playerCount;
        let attempts = 0;

        while (attempts < playerCount) {
            const territoryCount = grid.filter(cell => cell === nextIndex).length;
            if (territoryCount > 0) {
                return nextIndex;
            }
            nextIndex = (nextIndex + 1) % playerCount;
            attempts++;
        }

        return 0;
    }, []);

    // #endregion

    // ============================================================================
    // MÜZİK KONTROLÜ
    // ============================================================================
    useEffect(() => {
        const musicUrl = '/sounds/gameplay.mp3';

        if (!gameMusicRef.current) {
            gameMusicRef.current = new Audio(musicUrl);
            gameMusicRef.current.loop = true;
            gameMusicRef.current.volume = 0.4;
        }

        if (!isMuted) {
            const playPromise = gameMusicRef.current.play();
            if (playPromise !== undefined) {
                playPromise.catch(error => {
                    console.warn("Oyun müziği otomatik çalınamadı:", error);
                });
            }
        } else {
            gameMusicRef.current.pause();
        }

        return () => {
            if (gameMusicRef.current) {
                gameMusicRef.current.pause();
                gameMusicRef.current.currentTime = 0;
            }
        };
    }, [isMuted]);

    useEffect(() => {
        if (gameMusicRef.current) {
            if (isMuted) {
                gameMusicRef.current.pause();
            } else {
                const playPromise = gameMusicRef.current.play();
                if (playPromise !== undefined) {
                    playPromise.catch(error => {
                        console.warn("Oyun müziği sessizden çıkarılınca çalınamadı:", error);
                    });
                }
            }
        }
    }, [isMuted]);

    useEffect(() => {
        if (gameMusicRef.current) {
            gameMusicRef.current.muted = isMuted;
        }
    }, [isMuted]);

    // ============================================================================
    // FIREBASE LISTENER
    // ============================================================================
    useEffect(() => {
        if (!roomCode || !currentUser) return;

        const roomRef = doc(db, 'rooms', roomCode);
        gameListenerRef.current = onSnapshot(roomRef, (snapshot) => {
            if (snapshot.exists()) {
                const roomData = snapshot.data();
                const currentGameState = roomData.gameState;
                const currentPlayers = roomData.players || [];

                const isGameStateChanged = JSON.stringify(currentGameState) !== JSON.stringify(prevGameStateRef.current);
                const arePlayersChanged = JSON.stringify(currentPlayers) !== JSON.stringify(prevPlayersRef.current);

                if (isGameStateChanged) {
                    setGameState(currentGameState);
                    setPlayerStats(currentGameState?.playerStats || {});
                    prevGameStateRef.current = currentGameState;

                    if (currentGameState?.winner && !showWinner) {
                        const winnerPlayer = currentPlayers.find(p => p.id === currentGameState.winner);
                        if (winnerPlayer) {
                            setWinner(winnerPlayer);
                            setShowWinner(true);
                        }
                    }
                }
                if (arePlayersChanged) {
                    setPlayers(currentPlayers);
                    prevPlayersRef.current = currentPlayers;
                }

                const myIndex = currentPlayers.findIndex(p => p.id === currentUser.uid);
                setMyPlayerIndex(myIndex);
            }
        });

        return () => {
            if (gameListenerRef.current) {
                gameListenerRef.current();
            }
        };
    }, [roomCode, currentUser]);

    // ============================================================================
    // BOT KONTROL
    // ============================================================================
    useEffect(() => {
        if (!gameState || !players.length || !executeBotAction || !executeBotSetup || myPlayerIndex === -1) {
            return;
        }

        const currentPlayerIndex = gameState.currentPlayerIndex;
        const currentPlayer = players[currentPlayerIndex];
        const isBotTurn = currentPlayer?.isBot;
        const isHost = myPlayerIndex === 0;

        if (!isHost || !isBotTurn || gameState.winner) {
            return;
        }

        if (botTimeoutRef.current) {
            clearTimeout(botTimeoutRef.current);
        }

        botTimeoutRef.current = setTimeout(async () => {
            const roomData = { gameState, players };
            const botCellsPlaced = gameState.grid.filter(c => c === currentPlayerIndex).length;

            if (gameState.turn === 0) {
                if (botCellsPlaced < INITIAL_CELLS) {
                    await executeBotSetup(currentPlayerIndex, roomData);
                }
            } else {
                await executeBotAction(currentPlayerIndex, roomData);
            }
        }, 600);

    }, [
        gameState?.currentPlayerIndex,
        gameState?.turn,
        gameState?.winner,
        myPlayerIndex,
        executeBotSetup,
        executeBotAction,
        players
    ]);

    // ✅ Zar sonucunu 3 saniye sonra otomatik kapat
    useEffect(() => {
        if (showDiceResult) {
            const timer = setTimeout(() => {
                setShowDiceResult(false);
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [showDiceResult]);

    // Ses efektleri
    useEffect(() => {
        if (showWinner && winner) {
            playSound('success', 1.0);
        }
    }, [showWinner, winner, playSound]);

    useEffect(() => {
        if (criticalHit) {
            playSound('critical-hit', 1.0);
        }
    }, [criticalHit, playSound]);

    // #region Aksiyon Fonksiyonları

    const confirmAndProcessEndTurn = useCallback(async () => {
        if (showEndTurnConfirmation) {
            setShowEndTurnConfirmation(false);
        }

        playSound('click', 0.4);
        try {
            const roomRef = doc(db, 'rooms', roomCode);
            const nextPlayer = (gameState.currentPlayerIndex + 1) % players.length;
            const nextTurn = nextPlayer === 0 ? gameState.turn + 1 : gameState.turn;

            const updatedStats = { ...playerStats };
            const inactiveBonus = 0.5;

            players.forEach((p, i) => {
                if (!updatedStats[i]) {
                    updatedStats[i] = { attackPower: 0, defensePower: 0, totalScore: 0 };
                }

                const territoryCount = gameState.grid.filter(c => c === i).length;
                if (territoryCount > 0 && i !== gameState.currentPlayerIndex && (gameState.attackCount === 0)) {
                    updatedStats[i].totalScore += inactiveBonus;
                    updatedStats[i].defensePower = Math.min(
                        5,
                        Math.floor(Math.log2(updatedStats[i].totalScore + 1) / 1.8)
                    );
                }
            });

            Object.keys(updatedStats).forEach(i => {
                updatedStats[i].attackPower = Math.max(0, updatedStats[i].attackPower * 0.8);
                updatedStats[i].defensePower = Math.max(0, updatedStats[i].defensePower * 0.85);
            });

            if (nextTurn % 3 === 0 && nextPlayer === 0) {
                const randomPlayer = Math.floor(Math.random() * players.length);
                const eventRoll = Math.random();

                if (eventRoll < 0.33) {
                    updatedStats[randomPlayer].totalScore = (updatedStats[randomPlayer]?.totalScore || 0) + 2;
                } else if (eventRoll < 0.66) {
                    const powerLevels = players.map((_, i) =>
                        (updatedStats[i]?.attackPower || 0) + (updatedStats[i]?.defensePower || 0)
                    );
                    const strongestIndex = powerLevels.indexOf(Math.max(...powerLevels));
                    if (strongestIndex !== -1) {
                        updatedStats[strongestIndex].attackPower *= 0.7;
                        updatedStats[strongestIndex].defensePower *= 0.7;
                    }
                }
            }

            const newGameState = {
                ...gameState,
                currentPlayerIndex: nextPlayer,
                turn: nextTurn,
                hasExpanded: false,
                attackCount: 0,
                playerStats: updatedStats
            };

            if (nextTurn >= MAX_TURNS && nextTurn > 0) {
                const territories = players.map((_, i) =>
                    gameState.grid.filter(c => c === i).length
                );
                const maxTerritory = Math.max(...territories);
                const winnerId = territories.indexOf(maxTerritory);
                const finalLog = [
                    ...(gameState.log || []),
                    `🏆 ${players[winnerId].name} oyunu kazandı! (${maxTerritory} kare ile, Tur limiti (${MAX_TURNS}) sonunda!)`
                ];

                newGameState.winner = players[winnerId].id;
                newGameState.log = finalLog.slice(-MAX_LOG_ENTRIES);

            }

            await updateDoc(roomRef, { gameState: newGameState });

            setSelectedCell(null);
            setSelectedSetupCells([]);
            setSelectableCells([]);

            // ✅ Toast ile bildirim
            showToast('Tur başarıyla bitirildi!', 'success', 2000);
        } catch (error) {
            console.error('Tur bitirme hatası:', error);
            showToast('Tur bitirme hatası: ' + error.message, 'error', 3000);
        }
    }, [gameState, players, roomCode, playSound, showEndTurnConfirmation, playerStats, showToast, MAX_TURNS]);

    const handleEndTurn = useCallback(async () => {
        if (gameState?.currentPlayerIndex !== myPlayerIndex) {
            showToast('Şu anda sıra sizde değil!', 'warning', 2500);
            return;
        }

        const remainingAttacks = getMaxAttacksForCurrentTurn() - (gameState.attackCount || 0);

        if (remainingAttacks > 0 || !gameState.hasExpanded) {
            setShowEndTurnConfirmation(true);
            return;
        }

        await confirmAndProcessEndTurn();
    }, [gameState, myPlayerIndex, getMaxAttacksForCurrentTurn, confirmAndProcessEndTurn, showToast]);

    const handlePlayerAttack = useCallback(async (attackerPrediction) => {
        if (!attackTarget || !gameState || !roomCode) return;

        const currentAttackCount = gameState.attackCount || 0;
        if (currentAttackCount >= getMaxAttacksForCurrentTurn()) {
            showToast(`Bu turda maksimum ${getMaxAttacksForCurrentTurn()} saldırı yapabilirsiniz!`, 'warning', 2500);
            setIsAwaitingPrediction(false);
            setAttackTarget(null);
            return;
        }

        const { from, to } = attackTarget;
        const roomRef = doc(db, "rooms", roomCode);

        const defenderPrediction = Math.floor(Math.random() * 6) + 1;
        const attackRoll = rollDice();
        const defenseRoll = rollDice();

        let attackerScore = 6 - Math.abs(attackerPrediction - attackRoll);
        let defenderScore = 6 - Math.abs(defenderPrediction - defenseRoll);

        attackerScore += 0.3;

        const attackerPower = playerStats[myPlayerIndex]?.attackPower || 0;
        const defenderIndex = gameState.grid[getIndex(to.x, to.y)];
        const defenderPower = playerStats[defenderIndex]?.defensePower || 0;

        const attackerEffect = attackerPower * 0.08;
        const defenderEffect = defenderPower * 0.06;

        attackerScore += attackerEffect;
        defenderScore += defenderEffect;

        const territories = players.map((_, i) => gameState.grid.filter(c => c === i).length);
        const minTerritory = Math.min(...territories.filter(t => t > 0));
        const myTerritory = territories[myPlayerIndex];
        const catchUpBonus = (myTerritory === minTerritory && myTerritory > 0) ? 0.5 : 0;

        const avgPower = players.reduce((sum, _, i) => sum + (playerStats[i]?.attackPower || 0), 0) / players.length;
        const myPower = playerStats[myPlayerIndex]?.attackPower || 0;
        const powerGapBonus = myPower < avgPower * 0.5 ? 0.6 : 0;

        const luckVariance = (Math.random() - 0.5) * 4;
        attackerScore += luckVariance;
        defenderScore -= luckVariance / 3;

        const critRoll = Math.random();
        let criticalHitOccurred = false;

        if (critRoll > 0.85) {
            attackerScore += 2.5;
            criticalHitOccurred = true;
            setCriticalHit(true);
            setTimeout(() => setCriticalHit(false), 1200);
        } else if (critRoll < 0.08) {
            attackerScore -= 1.5;
        }

        if (attackerPrediction === attackRoll) {
            attackerScore += 1.5;
        }
        if (defenderPrediction === defenseRoll) {
            defenderScore += 1.5;
        }

        attackerScore += catchUpBonus + powerGapBonus;

        const success = attackerScore >= defenderScore;
        const targetIdx = getIndex(to.x, to.y);
        const attackerIdx = getIndex(from.x, from.y);

        addBattle({
            from: attackTarget.from,
            to: attackTarget.to,
            success: success,
            criticalHit: criticalHitOccurred,
            attackerColor: players[myPlayerIndex].color,
            defenderColor: players[defenderIndex].color
        });

        const newGrid = [...gameState.grid];
        const lostCell = !success;

        if (success) {
            newGrid[targetIdx] = myPlayerIndex;
        } else {
            newGrid[attackerIdx] = defenderIndex;
        }

        const newAttackCount = (gameState.attackCount || 0) + 1;

        const resultMessage = success
            ? "💥 KAZANDI"
            : "💀 KAYBETTI ve karesini kaybetti!";

        const updatedStats = updatePlayerStatsAfterBattle(
            myPlayerIndex,
            defenderIndex,
            success ? attackerScore * 0.7 : attackerScore * 0.3,
            lostCell ? defenderScore * 1.2 : defenderScore * 0.7,
            gameState.playerStats || {}
        );

        const newLog = [
            ...(gameState.log || []),
            `⚔️ ${currentUser.displayName}: (${from.x},${from.y}) → (${to.x},${to.y}) ${resultMessage} (${attackRoll}-${defenseRoll}) [Atk:${attackerScore.toFixed(1)} / Def:${defenderScore.toFixed(1)}]${criticalHitOccurred ? ' 💥 KRİTİK!' : ''}`
        ];

        const newGameState = {
            ...gameState,
            grid: newGrid,
            attackCount: newAttackCount,
            playerStats: updatedStats,
            log: newLog.slice(-MAX_LOG_ENTRIES)
        };

        try {
            await updateDoc(roomRef, { gameState: newGameState });

            const battleResultText = success
                ? "✅ KAZANDIN!"
                : "❌ KAYBETTİN!";

            setDiceResult({
                attacker: attackRoll,
                defender: defenseRoll,
                attackerPrediction,
                defenderPrediction,
                attackerScore,
                defenderScore,
                success,
                isDraw: attackerScore === defenderScore,
                powerBonus: attackerEffect,
                criticalHit: criticalHitOccurred,
                lostCell: lostCell
            });

            setBattleResult(battleResultText);
            setShowDiceResult(true);

            if (success) {
                playSound('attack', 0.6);
            } else {
                playSound('defeat', 0.8);
            }

            setSelectedCell(null);
            setSelectableCells([]);
            setIsAwaitingPrediction(false);
            setAttackTarget(null);

            setTimeout(() => checkWinningConditions(newGameState), 500);
        } catch (error) {
            console.error("Oyuncu saldırısı hatası:", error);
            showToast("Saldırı hatası: " + error.message, 'error', 3000);
        }
    }, [attackTarget, gameState, roomCode, playerStats, myPlayerIndex, players, addBattle, updatePlayerStatsAfterBattle, currentUser, playSound, checkWinningConditions, getMaxAttacksForCurrentTurn, rollDice, getIndex, showToast]);

    const handleExpand = useCallback(async () => {
        if (!selectedCell?.target || !gameState || !roomCode) return;

        try {
            const roomRef = doc(db, 'rooms', roomCode);
            const targetIndex = getIndex(selectedCell.target.x, selectedCell.target.y);

            if (gameState.grid[targetIndex] !== -1) {
                showToast('Hedef hücre boş olmalı!', 'error', 2500);
                return;
            }
            if (gameState.hasExpanded) {
                showToast('Bu tur zaten genişlediniz!', 'warning', 2500);
                return;
            }

            const newGrid = [...gameState.grid];
            newGrid[targetIndex] = myPlayerIndex;

            const newLog = [
                ...(gameState.log || []),
                `${players[myPlayerIndex].name} (${selectedCell.x},${selectedCell.y}) → (${selectedCell.target.x},${selectedCell.target.y}) 📦 YAYILDI`
            ];

            const newGameState = {
                ...gameState,
                grid: newGrid,
                hasExpanded: true,
                log: newLog.slice(-MAX_LOG_ENTRIES)
            };

            await updateDoc(roomRef, { gameState: newGameState });

            setSelectedCell(null);
            setSelectableCells([]);
            playSound('expand', 0.5);
            showToast('Bölge başarıyla genişletildi!', 'success', 2000);
        } catch (error) {
            console.error('Genişleme hatası:', error);
            showToast('Genişleme hatası: ' + error.message, 'error', 3000);
        }
    }, [selectedCell, gameState, roomCode, getIndex, myPlayerIndex, players, playSound, showToast]);

    const handleConfirmSetup = useCallback(async () => {
        if (selectedSetupCells.length !== INITIAL_CELLS) {
            showToast(`Lütfen ${INITIAL_CELLS} kare seçin!`, 'error', 2500);
            return;
        }

        try {
            const roomRef = doc(db, 'rooms', roomCode);
            const newGrid = [...gameState.grid];

            selectedSetupCells.forEach(cell => {
                newGrid[getIndex(cell.x, cell.y)] = myPlayerIndex;
            });

            const nextPlayer = (gameState.currentPlayerIndex + 1) % players.length;
            const allCellsChosen = newGrid.filter(c => c >= 0).length >= players.length * INITIAL_CELLS;

            const newLog = [
                ...(gameState.log || []),
                `${players[myPlayerIndex].name} ${INITIAL_CELLS} karesini seçti.`
            ];
            const newGameState = {
                ...gameState,
                grid: newGrid,
                currentPlayerIndex: nextPlayer,
                playerStats: playerStats,
                log: newLog.slice(-MAX_LOG_ENTRIES),
            };

            const updates = {
                gameState: newGameState
            };

            if (allCellsChosen) {
                newGameState.turn = 1;
                newGameState.hasExpanded = false;
                newGameState.attackCount = 0;

                const finalLog = [...newGameState.log, '🎲 Oyun Başladı! Tur 1.'];
                newGameState.log = finalLog.slice(-MAX_LOG_ENTRIES);

                updates.status = 'playing';
            }

            await updateDoc(roomRef, updates);

            setSelectedSetupCells([]);
            playSound('success', 0.6);
            showToast('Kurulum başarıyla tamamlandı!', 'success', 2000);

        } catch (error) {
            console.error('Kurulum hatası:', error);
            showToast('Kurulum hatası: ' + error.message, 'error', 3000);
        }
    }, [selectedSetupCells, roomCode, gameState, getIndex, myPlayerIndex, players, playerStats, playSound, showToast]);

    const handleAttack = useCallback(() => {
        if (!selectedCell?.target) return;

        const targetIndex = getIndex(selectedCell.target.x, selectedCell.target.y);
        const targetOwner = gameState.grid[targetIndex];

        if (!canPerformAttack()) {
            showToast(`Bu turda maksimum ${getMaxAttacksForCurrentTurn()} saldırı yapabilirsiniz!`, 'warning', 2500);
            return;
        }
        if (targetOwner === -1 || targetOwner === myPlayerIndex) {
            showToast('Geçersiz hedef!', 'error', 2500);
            return;
        }

        setAttackTarget({ from: selectedCell, to: selectedCell.target });
        setIsAwaitingPrediction(true);

        setSelectedCell(null);
        setSelectableCells([]);
    }, [selectedCell, gameState, canPerformAttack, getMaxAttacksForCurrentTurn, myPlayerIndex, getIndex, showToast]);

    // #region Cell Click Handlers
    const handleSetupCellClick = useCallback((x, y) => {
        if (!gameState || gameState.turn !== 0) return;
        if (gameState.currentPlayerIndex !== myPlayerIndex) {
            showToast('Şu anda sıra sizde değil!', 'warning', 2000);
            return;
        }

        const cellIndex = getIndex(x, y);
        if (gameState.grid[cellIndex] !== -1) {
            showToast('Bu hücre zaten seçilmiş!', 'warning', 2000);
            return;
        }

        const isAlreadySelected = selectedSetupCells.some(c => c.x === x && c.y === y);

        setSelectedSetupCells(prev => {
            if (isAlreadySelected) {
                return prev.filter(c => !(c.x === x && c.y === y));
            } else if (prev.length < INITIAL_CELLS) {
                return [...prev, { x, y }];
            }
            return prev;
        });
    }, [gameState, myPlayerIndex, selectedSetupCells, getIndex, showToast]);

    const handleGameCellClick = useCallback((x, y) => {
        if (!gameState || gameState.currentPlayerIndex !== myPlayerIndex) {
            showToast('Şu anda sıra sizde değil!', 'warning', 2000);
            return;
        }

        const owner = gameState.grid[getIndex(x, y)];

        if (owner === myPlayerIndex) {
            setSelectedCell({ x, y });
            const adjacent = [
                { x: x - 1, y }, { x: x + 1, y },
                { x, y: y - 1 }, { x, y: y + 1 }
            ].filter(coord =>
                coord.x >= 0 && coord.x < GRID_W &&
                coord.y >= 0 && coord.y < GRID_H
            );
            setSelectableCells(adjacent);
        }
        else if (selectedCell && selectableCells.some(cell => cell.x === x && cell.y === y)) {
            setSelectedCell({ ...selectedCell, target: { x, y } });
            setSelectableCells([]);
        }
        else {
            setSelectedCell(null);
            setSelectableCells([]);
        }
    }, [gameState, myPlayerIndex, selectedCell, selectableCells, getIndex, showToast]);

    const handleCellClick = useCallback((x, y) => {
        playSound('click', 0.3);
        if (gameState?.turn === 0) {
            handleSetupCellClick(x, y);
        } else {
            handleGameCellClick(x, y);
        }
    }, [gameState?.turn, playSound, handleSetupCellClick, handleGameCellClick]);

    const handleLeaveGame = useCallback(async () => {
        // ✅ Onay modalı yerine Toast ile uyarı + confirm dialog
        const confirmed = window.confirm('Oyundan ayrılmak istediğinize emin misiniz? Bu işlem geri alınamaz.');
        
        if (!confirmed) return;

        try {
            if (gameState && gameState.turn > 0 && !gameState.winner) {
                const roomRef = doc(db, 'rooms', roomCode);
                const newGrid = gameState.grid.map(cell =>
                    cell === myPlayerIndex ? -1 : cell
                );

                const newGameState = {
                    ...gameState,
                    grid: newGrid,
                    log: [...gameState.log, `🚪 ${players[myPlayerIndex]?.name} oyundan ayrıldı!`]
                };

                if (gameState.currentPlayerIndex === myPlayerIndex) {
                    const nextPlayer = findNextActivePlayer(myPlayerIndex, newGrid, players.length);
                    newGameState.currentPlayerIndex = nextPlayer;
                }

                await updateDoc(roomRef, { gameState: newGameState });

                await leaveGame(roomCode, currentUser.uid);
            } else {
                await leaveGame(roomCode, currentUser.uid);
            }

            localStorage.removeItem('axiati_room_code');
            localStorage.removeItem('axiati_screen');

            setTimeout(() => onGameEnd(), 500);
        } catch (error) {
            console.error('Ayrılma hatası:', error);
            localStorage.removeItem('axiati_room_code');
            localStorage.removeItem('axiati_screen');
            onGameEnd();
        }
    }, [gameState, roomCode, myPlayerIndex, players, findNextActivePlayer, leaveGame, currentUser, onGameEnd]);
    // #endregion

    // Hesaplanmış değerler
    const isMyTurn = gameState?.currentPlayerIndex === myPlayerIndex;
    const isSetupPhase = gameState?.turn === 0;
    const attackCount = gameState?.attackCount || 0;
    const currentTurnPlayer = players[gameState?.currentPlayerIndex];

    // ============================================================================
    // GRID CELLS MEMOİZE
    // ============================================================================
    const gridCells = useMemo(() => {
        if (!gameState?.grid) return [];

        const GRID_W = 12;
        const GRID_H = 9;

        const borderKeys = new Array(gameState.grid.length);

        for (let index = 0; index < gameState.grid.length; index++) {
            const owner = gameState.grid[index];

            if (owner === -1) {
                borderKeys[index] = 'empty';
                continue;
            }

            const x = index % GRID_W;
            const y = Math.floor(index / GRID_W);

            const top = (y === 0 || gameState.grid[(y - 1) * GRID_W + x] !== owner);
            const right = (x === GRID_W - 1 || gameState.grid[y * GRID_W + (x + 1)] !== owner);
            const bottom = (y === GRID_H - 1 || gameState.grid[(y + 1) * GRID_W + x] !== owner);
            const left = (x === 0 || gameState.grid[y * GRID_W + (x - 1)] !== owner);

            borderKeys[index] = `${top ? 't' : ''}${right ? 'r' : ''}${bottom ? 'b' : ''}${left ? 'l' : ''}`;
        }

        return gameState.grid.map((owner, index) => {
            const x = index % GRID_W;
            const y = Math.floor(index / GRID_W);

            const isSetupSelected = selectedSetupCells.some(c => c.x === x && c.y === y);
            const isSelected = selectedCell?.x === x && selectedCell?.y === y;
            const isTarget = selectedCell?.target?.x === x && selectedCell?.target?.y === y;
            const isSelectable = selectableCells.some(c => c.x === x && c.y === y);

            let isSelectableEnemy = false;
            let isSelectableEmpty = false;

            if (isSelectable) {
                const targetOwner = gameState.grid[getIndex(x, y)];
                if (targetOwner === -1 && !gameState.hasExpanded) {
                    isSelectableEmpty = true;
                } else if (targetOwner >= 0 && targetOwner !== myPlayerIndex && (gameState.attackCount || 0) < getMaxAttacksForCurrentTurn()) {
                    isSelectableEnemy = true;
                }
            }

            return {
                index,
                owner,
                x,
                y,
                borderKey: borderKeys[index],
                isSetupSelected,
                isSelected,
                isTarget,
                isSelectableEnemy,
                isSelectableEmpty
            };
        });
    }, [
        gameState?.grid,
        gameState?.hasExpanded,
        gameState?.attackCount,
        selectedSetupCells,
        selectedCell,
        selectableCells,
        myPlayerIndex,
        getIndex,
        getMaxAttacksForCurrentTurn
    ]);

    if (!gameState || !players.length || myPlayerIndex === -1) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
                <style>{`
                @keyframes fadeInScale {
                    from {
                        opacity: 0;
                        transform: scale(0.95);
                    }
                    to {
                        opacity: 1;
                        transform: scale(1);
                    }
                }
                
                .fade-in-scale {
                    animation: fadeInScale 0.5s ease-out;
                }

                @keyframes pulse-slow {
                    0%, 100% {
                        opacity: 1;
                        transform: scale(1);
                    }
                    50% {
                        opacity: 0.8;
                        transform: scale(1.05);
                    }
                }

                .loading-logo {
                    animation: pulse-slow 2s ease-in-out infinite;
                }

                .loading-logo-image {
                    max-width: 280px;
                    width: 100%;
                    height: auto;
                    filter: drop-shadow(0 0 30px rgba(59, 130, 246, 0.4));
                }
            `}</style>

                <div className="text-center fade-in-scale">
                    <div className="mb-6 loading-logo">
                        <img
                            src="/logo-transparent.png"
                            alt="Axio War Logo"
                            className="loading-logo-image mx-auto"
                        />
                    </div>

                    <div className="space-y-3">
                        <div className="text-white text-xl font-semibold">Oyuna Bağlanılıyor</div>
                        <div className="text-slate-400 text-sm">Odaya bağlanılıyor, lütfen bekleyin...</div>

                        <div className="flex justify-center gap-1 mt-6">
                            <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0s' }}></div>
                            <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                            <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="game-screen flex flex-col h-screen w-screen bg-app-gradient text-white overflow-hidden font-sans">
            {/* ✅ Toast Container eklendi */}
            <ToastContainer toasts={toasts} removeToast={removeToast} />
            
            <header className="glass-header h-16 flex-shrink-0 z-50">
                <div className="h-full px-6 flex justify-between items-center max-w-[1920px] mx-auto w-full">
                    <div className="flex items-center gap-4">
                        <div className="logo-container-header pt-5">
                            <img
                                src="/logo-header.png"
                                alt="Axio War Logo"
                                className="logo-image"
                            />
                        </div>
                        <div>
                            <h1 className="text-lg font-bold text-white tracking-tight">ONLINE STRATEJİ OYUNU</h1>
                            <div className="flex items-center gap-2 text-xs text-slate-400">
                                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                                <span>beta: v1.23 • Canlı Oyun • Oda: {roomCode}</span>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-3 bg-slate-900/50 p-1.5 rounded-xl border border-slate-700/50 backdrop-blur-sm">
                        <SoundControl isMuted={isMuted} onToggleMute={toggleMute} />
                        <div className="h-4 w-px bg-slate-700 mx-1"></div>
                        <button onClick={toggleFullscreen} className="p-2 text-slate-400 hover:text-white hover:bg-slate-700/50 rounded-lg transition-colors">
                            {isFullscreen ? '↙' : '↗'}
                        </button>
                    </div>
                </div>
            </header>

            <div className="flex-1 flex overflow-hidden p-4 gap-4 max-w-[1920px] mx-auto w-full">

                {/* 🔥 SOL PANEL - GÜNCELLENDİ (Inline kartlar kaldırıldı, GameLog eklendi) */}
                <aside className="hidden lg:block w-72 flex-shrink-0 fade-in-scale overflow-y-auto">
                    {isSetupPhase ? (
                        <SetupInfoPanel
                            gameState={gameState}
                            players={players}
                            myPlayerIndex={myPlayerIndex}
                            selectedSetupCells={selectedSetupCells}
                        />
                    ) : (
                        <div className="h-full flex flex-col">
                            <div className="flex-shrink-0">
                                <PlayerList
                                    gameState={gameState}
                                    players={players}
                                    myPlayerIndex={myPlayerIndex}
                                    playerStats={playerStats}
                                    getMaxAttacksForCurrentTurn={getMaxAttacksForCurrentTurn}
                                />
                            </div>
                            
                            {/* 🔥 GÜNLÜK (LOG) BURAYA EKLENDİ */}
                            <GameLog gameState={gameState} />
                        </div>
                    )}
                </aside>

                <div className="flex-1 relative flex flex-col min-w-0">
                    <div className="glass-panel rounded-2xl flex-1 p-1 overflow-hidden relative shadow-2xl flex items-center justify-center">
                      <div
  style={{
    display: 'grid',
    gridTemplateColumns: `repeat(${GRID_W}, 1fr)`,
    gridTemplateRows: `repeat(${GRID_H}, 1fr)`,
    width: '100%',
    height: '100%',
    aspectRatio: `${GRID_W}/${GRID_H}`,
    maxWidth: '100%',
    maxHeight: '100%',
    ...mapBackgroundStyle,
    borderRadius: '12px',
    position: 'relative'
  }}
>
  {gridCells.map((cell) => (
    <GridCell
      key={cell.index}
      owner={cell.owner}
      x={cell.x}
      y={cell.y}
      borderKey={cell.borderKey}
      isSetupSelected={cell.isSetupSelected}
      isSelected={cell.isSelected}
      isTarget={cell.isTarget}
      isSelectableEnemy={cell.isSelectableEnemy}
      isSelectableEmpty={cell.isSelectableEmpty}
      isMyTurn={isMyTurn}
      isSetupPhase={isSetupPhase}
      onCellClick={handleCellClick}
      players={players}
      gameGrid={gameState?.grid}
    />
  ))}
  <DynamicMapOverlay gameState={gameState} players={players} />
  <BattleAnimation battle={currentBattle} onComplete={completeBattle} />
</div>
                    </div>
                    <div className="absolute pt-0 left-1/2 transform -translate-x-1/2 pointer-events-none">
                        <div className="glass-panel px-6 py-2 rounded-full flex items-center gap-3 shadow-lg border-t border-white/10">
                            <span className="text-red-400 text-lg font-bold uppercase tracking-widest">TUR {gameState.turn}</span>
                            <div className="w-px h-4 bg-slate-600"></div>
                            {isMyTurn ? (
                                <span className="text-emerald-400 text-sm font-bold animate-pulse">Sıra Sende!</span>
                            ) : (
                                <span className="text-slate-300 text-sm font-medium">{currentTurnPlayer?.name} oynuyor...</span>
                            )}
                        </div>
                    </div>
                </div>
                
                {/* 🔥 SAĞ PANEL - GÜNCELLENDİ (Inline kartlar buraya eklendi) */}
                <aside className="hidden lg:block w-72 flex-shrink-0 fade-in-scale">
                    <div className="flex flex-col gap-2">
                         <GameControls
                            gameState={gameState}
                            isSetupPhase={isSetupPhase}
                            isMyTurn={isMyTurn}
                            selectedSetupCells={selectedSetupCells}
                            selectedCell={selectedCell}
                            currentTurnPlayer={currentTurnPlayer}
                            canPerformAttack={canPerformAttack}
                            handleConfirmSetup={handleConfirmSetup}
                            handleExpand={handleExpand}
                            handleAttack={handleAttack}
                            handleEndTurn={handleEndTurn}
                            handleLeaveGame={handleLeaveGame}
                            myPlayerIndex={myPlayerIndex}
                        />

                        {/* ✅ TAHMİN KARTI - SAĞ PANELE GELDİ */}
                        {!isSetupPhase && (
                            <InlinePredictionCard
                                onPredict={handlePlayerAttack}
                                onCancel={() => {
                                    setIsAwaitingPrediction(false);
                                    setAttackTarget(null);
                                }}
                                isVisible={isAwaitingPrediction}
                            />
                        )}

                        {/* ✅ ZAR SONUÇ KARTI - SAĞ PANELE GELDİ */}
                        {!isSetupPhase && (
                             <InlineDiceResultCard
                                diceResult={diceResult}
                                battleResult={battleResult}
                                isVisible={showDiceResult}
                            />
                        )}
                    </div>
                </aside>

                <div className="sidebar-content lg:hidden bg-black bg-opacity-40 backdrop-blur-sm rounded-lg border border-blue-500 border-opacity-20 p-1 overflow-hidden grid grid-cols-1 md:grid-cols-2 gap-2">
                    <div className="h-full overflow-y-auto">
                        {isSetupPhase ? (
                            <SetupInfoPanel
                                gameState={gameState}
                                players={players}
                                myPlayerIndex={myPlayerIndex}
                                selectedSetupCells={selectedSetupCells}
                            />
                        ) : (
                            <div className="flex flex-col">
                                <div className="flex-shrink-0">
                                    <PlayerList
                                        gameState={gameState}
                                        players={players}
                                        myPlayerIndex={myPlayerIndex}
                                        playerStats={playerStats}
                                        getMaxAttacksForCurrentTurn={getMaxAttacksForCurrentTurn}
                                    />
                                </div>
                                
                                {/* MOBİL GÖRÜNÜM İÇİN DE GÜNCELLENDİ */}
                                <div className="flex-shrink-0 mt-2">
                                     <GameLog gameState={gameState} />
                                </div>
                            </div>
                        )}
                    </div>
                    
                    {/* MOBİL GÖRÜNÜM SAĞ TARAF */}
                    <div className="h-full overflow-hidden flex flex-col gap-2">
                        <GameControls
                            gameState={gameState}
                            isSetupPhase={isSetupPhase}
                            isMyTurn={isMyTurn}
                            selectedSetupCells={selectedSetupCells}
                            selectedCell={selectedCell}
                            currentTurnPlayer={currentTurnPlayer}
                            canPerformAttack={canPerformAttack}
                            handleConfirmSetup={handleConfirmSetup}
                            handleExpand={handleExpand}
                            handleAttack={handleAttack}
                            handleEndTurn={handleEndTurn}
                            handleLeaveGame={handleLeaveGame}
                            myPlayerIndex={myPlayerIndex}
                        />
                         {!isSetupPhase && (
                            <>
                                <InlinePredictionCard
                                    onPredict={handlePlayerAttack}
                                    onCancel={() => {
                                        setIsAwaitingPrediction(false);
                                        setAttackTarget(null);
                                    }}
                                    isVisible={isAwaitingPrediction}
                                />
                                <InlineDiceResultCard
                                    diceResult={diceResult}
                                    battleResult={battleResult}
                                    isVisible={showDiceResult}
                                />
                            </>
                         )}
                    </div>
                </div>
            </div>

            {/* ✅ Modallar sadece kritik durumlar için kaldı */}

            {showEndTurnConfirmation && (
                <EndTurnModal
                    gameState={gameState}
                    currentMaxAttacks={getMaxAttacksForCurrentTurn()}
                    onConfirm={confirmAndProcessEndTurn}
                    onCancel={() => setShowEndTurnConfirmation(false)}
                    isOpen={showEndTurnConfirmation}
                />
            )}

            {showWinner && winner && (
                <WinnerModal
                    winnerName={winner.name}
                    onClose={onGameEnd}
                    isOpen={showWinner}
                />
            )}

            {eliminatedPlayer && (
                <EliminationModal
                    playerName={eliminatedPlayer}
                    onClose={() => setEliminatedPlayer(null)}
                    isOpen={!!eliminatedPlayer}
                />
            )}
            {showSelfElimination && (
                <SelfEliminationModal
                    onClose={onGameEnd}
                    isOpen={showSelfElimination}
                />
            )}

            {criticalHit && (
                <div className="critical-hit-overlay">
                    💥 Kritik Vuruş!
                </div>
            )}

        </div>
    );
};

export default React.memo(GameScreen);